package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 解析 fun/lambda/控制流内部语句块（历史上也承载过 action 体解析）。
 *
 * 支持的语句（按首 token 分派）：
 * - 局部变量声明 `var NAME = expr`
 * - 控制流 `if`/`while`
 * - 跳出 `break` / `continue` / `return [expr]`
 * - 异常处理 `try { } catch (e) { } [finally { }]`
 * - 赋值 `state.<name> = expr` 或 `<local> = expr`
 * - 表达式语句（仅调用形式，含 `net.request(...)` / `await net.request(...)`）
 */
internal class ActionBlockParser(private val p: Parser) {

    companion object {
        /** `suspend` 是软关键字（词法层为 IDENTIFIER），与 TypeParser / ScreenParser 约定一致。 */
        private const val SOFT_KW_SUSPEND = "suspend"

        /** `batch` 是软关键字（词法层为 IDENTIFIER）：`batch { ... }` 合批写入块。 */
        private const val SOFT_KW_BATCH = "batch"
    }

    /**
     * 解析一段 stmt 列表直到 [end] 或 EOF。
     *
     * 语句之间使用 NEWLINE 分隔；允许语句末尾立即遇到 [end]。
     *
     * @param stmtParser 单条语句解析器，默认走标准 [parseStmt]。
     *        when 分支体走 [parseBranchStmt]、lambda 体走 [parseLambdaStmt]。
     */
    internal fun parseStmtListUntil(
        end: TokenType,
        allowBareIdent: Boolean,
        stmtParser: (Boolean) -> StmtAst = ::parseStmt,
    ): List<StmtAst> {
        val out = mutableListOf<StmtAst>()
        p.skipNewlines()
        while (!p.check(end) && !p.check(TokenType.EOF)) {
            out += stmtParser(allowBareIdent)
            if (p.check(end) || p.check(TokenType.EOF)) break
            p.expect(TokenType.NEWLINE, "语句后缺少换行")
            p.skipNewlines()
        }
        return out
    }

    /**
     * 进入一个块级作用域并执行 [body]，无论正常返回或异常都保证 popBlockScope。
     *
     * 用于 parseBlock / parseBranchBody / parseLambdaBody / catch 体 等所有"块作用域 + 语句序列"的场景，
     * 集中 push/pop 与可选的 returnable 开关，避免 try-finally 模板分散。
     *
     * @param enterReturnable 块内是否允许 `return`（lambda 体打开；普通 block 关闭）
     * @param setup 进入作用域后的初始化（如声明 lambda 形参、注册循环变量）
     */
    private inline fun <T> withScopedBlock(
        enterReturnable: Boolean = false,
        setup: () -> Unit = {},
        body: () -> T,
    ): T {
        p.pushBlockScope()
        setup()
        if (enterReturnable) p.enterReturnable()
        return try {
            body()
        } finally {
            if (enterReturnable) p.exitReturnable()
            p.popBlockScope()
        }
    }

    /**
     * 解析单条语句。
     *
     * @param allowBareIdent 是否允许表达式里出现裸标识符引用（仅 request 回调体打开）
     */
    internal fun parseStmt(allowBareIdent: Boolean): StmtAst {
        return when (p.current().type) {
            TokenType.KW_VAR -> parseVarDecl(allowBareIdent)
            TokenType.KW_FUN -> parseLocalFunDecl(isSuspend = false)
            TokenType.KW_IF -> parseIf(allowBareIdent)
            TokenType.KW_WHILE -> parseWhile(allowBareIdent)
            TokenType.KW_FOR -> parseForIn(allowBareIdent)
            TokenType.KW_WHEN -> parseWhenStmt(allowBareIdent)
            TokenType.KW_BREAK -> parseBreak()
            TokenType.KW_CONTINUE -> parseContinue()
            TokenType.KW_RETURN -> parseReturn(allowBareIdent)
            TokenType.KW_TRY -> parseTry(allowBareIdent)
            TokenType.KW_STATE -> parseStateAssign(allowBareIdent)
            TokenType.KW_SESSION -> parseScopedAssign(
                TokenType.KW_SESSION, "session", allowBareIdent,
            )
            TokenType.KW_PERSIST -> parseScopedAssign(
                TokenType.KW_PERSIST, "persist", allowBareIdent,
            )
            TokenType.KW_DERIVED -> p.fail(
                "derived 派生值是只读的，不能赋值或作为语句出现",
                p.current().position,
            )
            TokenType.IDENTIFIER -> parseIdentStmt(allowBareIdent)
            TokenType.KW_AWAIT -> parseExprStmt(allowBareIdent)
            else -> p.fail("意外的 token: '${p.current().lexeme}'")
        }
    }

    /**
     * 解析块内局部 `fun NAME(params) { body }`（M5-e 起也支持 `suspend fun`）。
     *
     * 把 name 先注册到当前块的 localStack（与 var 共用，参与就近解析），
     * 这样既支持声明后可见、也允许 body 内递归调用自身。
     *
     * @param isSuspend 调用方已识别并消费过 `suspend` 软关键字前缀时传入 true。
     */
    private fun parseLocalFunDecl(isSuspend: Boolean): StmtAst.FunDeclStmt {
        // 预取 name 注册到外层 block 作用域
        val nameTok = p.peek(1)
        if (nameTok.type != TokenType.IDENTIFIER) {
            p.fail("fun 后缺少函数名", nameTok.position)
        }
        if (!p.declareLocal(nameTok.lexeme)) {
            p.fail("局部 fun '${nameTok.lexeme}' 与同名声明冲突", nameTok.position)
        }
        return FunDeclParser(p).parseLocal(isSuspend)
    }

    /** 解析 `{ stmt* }` 块；包含作用域 push/pop。 */
    private fun parseBlock(allowBareIdent: Boolean): List<StmtAst> {
        p.expect(TokenType.LBRACE, "缺少 '{'")
        p.skipNewlines()
        val body = withScopedBlock {
            parseStmtListUntil(TokenType.RBRACE, allowBareIdent)
        }
        p.expect(TokenType.RBRACE, "缺少 '}'")
        return body
    }

    private fun parseVarDecl(allowBareIdent: Boolean): StmtAst.VarDeclStmt {
        val varTok = p.expect(TokenType.KW_VAR)
        val nameTok = p.expect(TokenType.IDENTIFIER, "var 后缺少变量名")
        // M5-c：可选类型标注 `: Type`
        val typeAnnotation = if (p.check(TokenType.COLON)) {
            p.advance()
            TypeParser.parse(p)
        } else null
        p.expect(TokenType.EQUALS, "var 声明缺少 '='")
        val init = expr(allowBareIdent).parse()
        if (!p.declareLocal(nameTok.lexeme)) {
            p.fail("局部变量 '${nameTok.lexeme}' 重复声明", nameTok.position)
        }
        return StmtAst.VarDeclStmt(
            name = nameTok.lexeme,
            initializer = init,
            position = varTok.position.endingAt(init.range.end),
            typeAnnotation = typeAnnotation,
        )
    }

    /** 创建支持 when 表达式与 lambda 的 ExpressionParser。 */
    private fun expr(allowBareIdent: Boolean): ExpressionParser =
        ExpressionParser(
            p,
            allowBareIdent,
            { parseWhenExprNode(allowBareIdent) },
            { params -> parseLambdaBody(params, allowBareIdent) },
        )

    /**
     * 公开入口：返回一个支持 when / lambda 的 ExpressionParser，供其他 Parser 解析独立表达式。
     *
     * 当前用于 [ScreenParser] 解析 `onKey { event -> ... }` 中的独立 lambda；后续如有
     * 类似"在 screen 顶层声明的表达式式语法"也可复用。
     */
    internal fun expressionParser(allowBareIdent: Boolean = false): ExpressionParser =
        expr(allowBareIdent)

    private fun parseIf(allowBareIdent: Boolean): StmtAst.IfStmt {
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "if 后缺少 '('")
        val cond = expr(allowBareIdent).parse()
        p.expect(TokenType.RPAREN, "if 条件缺少 ')'")
        p.skipNewlines()
        val thenBody = parseBlock(allowBareIdent)
        // else / else if
        var elseBody: List<StmtAst> = emptyList()
        // 允许 `} else` 之间出现 NEWLINE
        val saved = p
        // 偷看：跳过 NEWLINE 看是否是 else
        val newlineSeen = p.check(TokenType.NEWLINE)
        if (newlineSeen) {
            // 先临时不消费；只有真的是 else 才跳
            var k = 0
            while (p.peek(k).type == TokenType.NEWLINE) k++
            if (p.peek(k).type == TokenType.KW_ELSE) {
                p.skipNewlines()
            }
        }
        if (p.check(TokenType.KW_ELSE)) {
            p.advance()
            elseBody = if (p.check(TokenType.KW_IF)) {
                listOf(parseIf(allowBareIdent))
            } else {
                p.skipNewlines()
                parseBlock(allowBareIdent)
            }
        }
        // saved 仅占位避免误删
        check(saved === p)
        return StmtAst.IfStmt(cond, thenBody, elseBody, p.completePosition(ifTok.position))
    }

    private fun parseWhile(allowBareIdent: Boolean): StmtAst.WhileStmt {
        val whileTok = p.expect(TokenType.KW_WHILE)
        p.expect(TokenType.LPAREN, "while 后缺少 '('")
        val cond = expr(allowBareIdent).parse()
        p.expect(TokenType.RPAREN, "while 条件缺少 ')'")
        p.skipNewlines()
        p.enterLoop()
        val body = try {
            parseBlock(allowBareIdent)
        } finally {
            p.exitLoop()
        }
        return StmtAst.WhileStmt(cond, body, p.completePosition(whileTok.position))
    }

    private fun parseBreak(): StmtAst.BreakStmt {
        val tok = p.expect(TokenType.KW_BREAK)
        if (!p.inLoop()) p.fail("'break' 仅可用于 while 循环体内", tok.position)
        return StmtAst.BreakStmt(tok.position)
    }

    private fun parseContinue(): StmtAst.ContinueStmt {
        val tok = p.expect(TokenType.KW_CONTINUE)
        if (!p.inLoop()) p.fail("'continue' 仅可用于 while 循环体内", tok.position)
        return StmtAst.ContinueStmt(tok.position)
    }

    private fun parseReturn(allowBareIdent: Boolean): StmtAst.ReturnStmt {
        val tok = p.expect(TokenType.KW_RETURN)
        if (!p.inReturnable()) p.fail("'return' 仅可用于 action / callback 体内", tok.position)
        // 后续若是表达式 token 则带返回值；否则视为裸 return
        val value: ExprAst? = if (isExprStart(p.current().type)) {
            expr(allowBareIdent).parse()
        } else null
        return StmtAst.ReturnStmt(value, p.completePosition(tok.position))
    }

    private fun parseTry(allowBareIdent: Boolean): StmtAst.TryStmt {
        val tryTok = p.expect(TokenType.KW_TRY)
        p.skipNewlines()
        val tryBody = parseBlock(allowBareIdent)
        p.skipNewlines()
        p.expect(TokenType.KW_CATCH, "try 必须跟随 catch 块")
        p.expect(TokenType.LPAREN, "catch 后缺少 '('")
        val nameTok = p.expect(TokenType.IDENTIFIER, "catch 缺少异常绑定名")
        p.expect(TokenType.RPAREN, "catch 缺少 ')'")
        p.skipNewlines()
        // catch 体内引入异常名局部
        val catchBody = withScopedBlock(setup = { p.declareLocal(nameTok.lexeme) }) {
            p.expect(TokenType.LBRACE, "catch 缺少 '{'")
            p.skipNewlines()
            val list = parseStmtListUntil(TokenType.RBRACE, allowBareIdent)
            p.expect(TokenType.RBRACE, "catch 缺少 '}'")
            list
        }
        // 可选 finally
        var finallyBody: List<StmtAst> = emptyList()
        // 跨越可能的换行查看 finally
        var k = 0
        while (p.peek(k).type == TokenType.NEWLINE) k++
        if (p.peek(k).type == TokenType.KW_FINALLY) {
            p.skipNewlines()
            p.advance() // KW_FINALLY
            p.skipNewlines()
            finallyBody = parseBlock(allowBareIdent)
        }
        // D2-c：try / catch / finally 体内禁止 await（第一版限制）。状态机切分需要
        // 把状态边界穿过异常处理 frame，未实现；用户应把 await 拆出 try 外。
        rejectAwaitInTry(tryBody, tryTok.position)
        rejectAwaitInTry(catchBody, tryTok.position)
        rejectAwaitInTry(finallyBody, tryTok.position)
        return StmtAst.TryStmt(tryBody, nameTok.lexeme, catchBody, finallyBody, p.completePosition(tryTok.position))
    }

    /** 递归扫描语句列表，遇 AwaitExpr 即报 [DslError.E601_AWAIT_IN_TRY]。 */
    private fun rejectAwaitInTry(stmts: List<StmtAst>, tryPos: SourcePosition) {
        for (s in stmts) if (containsAwait(s)) p.fail(DslError.E601_AWAIT_IN_TRY, tryPos)
    }

    /**
     * 解析 `batch { stmts }` 合批写入块。
     *
     * 复用 [parseBlock] 解析花括号内语句序列；batch 不引入命名绑定。
     * 块内禁止 await（合批需要同步执行）——递归扫描后报 [DslError.E609_AWAIT_IN_BATCH]。
     */
    private fun parseBatch(allowBareIdent: Boolean): StmtAst.BatchStmt {
        val batchTok = p.expect(TokenType.IDENTIFIER, "缺少 'batch'")
        p.skipNewlines()
        val body = parseBlock(allowBareIdent)
        for (s in body) if (containsAwait(s)) p.fail(DslError.E609_AWAIT_IN_BATCH, batchTok.position)
        return StmtAst.BatchStmt(body, p.completePosition(batchTok.position))
    }

    private fun containsAwait(s: StmtAst): Boolean = when (s) {
        is StmtAst.ExprStmt -> containsAwaitExpr(s.call)
        is StmtAst.AssignStmt -> containsAwaitExpr(s.rhs)
        is StmtAst.VarDeclStmt -> containsAwaitExpr(s.initializer)
        is StmtAst.IfStmt -> containsAwaitExpr(s.cond) ||
            s.thenBody.any { containsAwait(it) } || s.elseBody.any { containsAwait(it) }
        is StmtAst.WhileStmt -> containsAwaitExpr(s.cond) || s.body.any { containsAwait(it) }
        is StmtAst.ForInStmt -> containsAwaitExpr(s.iterable) || s.body.any { containsAwait(it) }
        is StmtAst.WhenStmt -> containsAwaitExpr(s.whenExpr)
        is StmtAst.ReturnStmt -> s.value?.let { containsAwaitExpr(it) } ?: false
        is StmtAst.TryStmt -> s.tryBody.any { containsAwait(it) } ||
            s.catchBody.any { containsAwait(it) } || s.finallyBody.any { containsAwait(it) }
        is StmtAst.BatchStmt -> s.body.any { containsAwait(it) }
        is StmtAst.FunDeclStmt -> false // 嵌套 fun 体走自己的 lowerer
        else -> false
    }

    private fun containsAwaitExpr(e: ExprAst): Boolean = when (e) {
        is ExprAst.AwaitExpr -> true
        is ExprAst.BinaryOp -> containsAwaitExpr(e.left) || containsAwaitExpr(e.right)
        is ExprAst.UnaryOp -> containsAwaitExpr(e.operand)
        is ExprAst.MemberAccess -> containsAwaitExpr(e.target)
        is ExprAst.SafeMemberAccess -> containsAwaitExpr(e.target)
        is ExprAst.MemberCall -> containsAwaitExpr(e.target) || e.args.any { containsAwaitExpr(it) }
                || e.namedArgs.any { containsAwaitExpr(it.value) }
        is ExprAst.SafeMemberCall -> containsAwaitExpr(e.target) || e.args.any { containsAwaitExpr(it) }
        is ExprAst.Elvis -> containsAwaitExpr(e.left) || containsAwaitExpr(e.right)
        is ExprAst.Ternary -> containsAwaitExpr(e.cond) || containsAwaitExpr(e.thenExpr) || containsAwaitExpr(e.elseExpr)
        is ExprAst.IfExpr -> containsAwaitExpr(e.cond) ||
            e.thenBody.any { containsAwait(it) } || e.elseBody.any { containsAwait(it) }
        is ExprAst.Interp -> e.parts.any { it is ExprAst.Interp.InterpPart.ExprPart && containsAwaitExpr(it.expr) }
        is ExprAst.ListLiteralExpr -> e.elements.any { containsAwaitExpr(it) }
        is ExprAst.RangeExpr -> containsAwaitExpr(e.start) || containsAwaitExpr(e.endValue)
        is ExprAst.IndexExpr -> containsAwaitExpr(e.target) || containsAwaitExpr(e.index)
        is ExprAst.CallExpr -> containsAwaitExpr(e.callee) || e.args.any { containsAwaitExpr(it) }
        is ExprAst.BuiltinCallExpr -> e.args.any { containsAwaitExpr(it) }
        is ExprAst.NetRequestCall -> e.payload.any { containsAwaitExpr(it.value) } ||
            (e.callback?.let { containsAwaitExpr(it) } ?: false) ||
            (e.timeoutMs?.let { containsAwaitExpr(it) } ?: false)
        is ExprAst.LambdaExpr -> false // lambda 体走独立 lowerer
        is ExprAst.WhenExpr -> containsAwaitExpr(e.subject) ||
            e.branches.any { br -> br.values.any { containsAwaitExpr(it) } || br.body.any { containsAwait(it) } } ||
            (e.elseBody?.any { containsAwait(it) } ?: false)
        else -> false
    }

    /** 是否为表达式可能的起始 token。 */
    private fun isExprStart(t: TokenType): Boolean = when (t) {
        TokenType.STRING, TokenType.NUMBER, TokenType.BOOLEAN, TokenType.NULL,
        TokenType.IDENTIFIER, TokenType.KW_STATE, TokenType.KW_SESSION, TokenType.KW_PERSIST,
        TokenType.KW_THEME,
        TokenType.INTERPOLATED_STRING_START, TokenType.LPAREN,
        TokenType.LBRACKET, TokenType.HASH_LBRACE, TokenType.KW_WHEN, TokenType.KW_IF,
        TokenType.MINUS, TokenType.BANG, TokenType.KW_AWAIT -> true
        else -> false
    }

    /**
     * 从当前 token（必须是 KW_IF）起前瞻：if (cond) 后的第一个非空 token 是否为 `{`。
     * <p>
     * - 返回 true：分支体是块语句，按 if 语句解析
     * - 返回 false：分支体是单表达式，按 if 表达式解析（{@link ExprAst.IfExpr}）
     * </p>
     * 仅前瞻，不消费 token。括号深度不平时退化为 true 让 if 语句路径接管报错。
     */
    private fun ifBranchLooksLikeBlock(): Boolean {
        // 跳过 KW_IF
        var k = 1
        if (p.peek(k).type != TokenType.LPAREN) return true
        k++
        var depth = 1
        while (depth > 0 && p.peek(k).type != TokenType.EOF) {
            when (p.peek(k).type) {
                TokenType.LPAREN -> depth++
                TokenType.RPAREN -> depth--
                else -> Unit
            }
            k++
            if (depth == 0) break
        }
        if (depth != 0) return true
        while (p.peek(k).type == TokenType.NEWLINE) k++
        return p.peek(k).type == TokenType.LBRACE
    }

    /**
     * 把 IDENTIFIER 起始的语句解析为：
     *
     * - `suspend fun NAME(...)` 局部 suspend fun（M5-e 软关键字）
     * - `<local> = expr` 局部变量赋值（若 IDENTIFIER 是已声明 var）
     * - 否则解析为表达式语句（必须是调用形式）
     */
    private fun parseIdentStmt(allowBareIdent: Boolean): StmtAst {
        val curTok = p.current()
        // M5-e：识别 `suspend fun` 软关键字前缀（suspend 在词法层仍是 IDENTIFIER）
        if (curTok.lexeme == SOFT_KW_SUSPEND && p.peek(1).type == TokenType.KW_FUN) {
            p.advance() // 消费 suspend
            return parseLocalFunDecl(isSuspend = true)
        }
        // `batch { ... }` 合批写入块（batch 在词法层为 IDENTIFIER，后紧跟 '{'）
        if (curTok.lexeme == SOFT_KW_BATCH && p.peek(1).type == TokenType.LBRACE) {
            return parseBatch(allowBareIdent)
        }
        if (isParamsAssignStart()) {
            parseParamsAssignReject()
        }
        // 局部变量赋值：IDENTIFIER + `=`
        if (p.isLocal(curTok.lexeme) && p.peek(1).type == TokenType.EQUALS) {
            p.advance()
            p.advance() // EQUALS
            val rhs = expr(allowBareIdent).parse()
            return StmtAst.AssignStmt(
                lhs = LValueAst.LocalLValue(curTok.lexeme, curTok.position),
                rhs = rhs,
                position = curTok.position.endingAt(rhs.range.end),
            )
        }
        return parseExprStmt(allowBareIdent)
    }

    /** 表达式语句仅接受调用形式，避免静默 no-op。 */
    private fun parseExprStmt(allowBareIdent: Boolean): StmtAst {
        val startTok = p.current()
        val parsed = expr(allowBareIdent).parse()
        // M6-a：`target[idx] = rhs` 索引赋值统一升格为 AssignStmt(IndexLValue)。
        tryIndexAssign(parsed, startTok.position, allowBareIdent)?.let { return it }
        // C5：net.request(...) 作为语句出现时合法（NetRequestCall 是 ExprAst 但表达"调用"语义）
        if (parsed !is ExprAst.MemberCall && parsed !is ExprAst.SafeMemberCall
            && parsed !is ExprAst.CallExpr && parsed !is ExprAst.NetRequestCall
            && parsed !is ExprAst.AwaitExpr && parsed !is ExprAst.BuiltinCallExpr) {
            p.fail("语句仅支持调用形式（name() / target.method(...) / target?.method(...)）", startTok.position)
        }
        return StmtAst.ExprStmt(parsed, startTok.position.endingAt(parsed.range.end))
    }

    /**
     * M6-a：若 [parsed] 是 IndexExpr 且当前 token 为 `=`，则升格为 AssignStmt(IndexLValue)。
     * 用于 parseExprStmt / parseLambdaStmt / parseBranchStmt 共享识别 `m["k"] = v` 写入语义。
     * 否则返回 null 由调用方继续按表达式语句处理。
     */
    private fun tryIndexAssign(
        parsed: ExprAst,
        stmtPos: SourcePosition,
        allowBareIdent: Boolean,
    ): StmtAst.AssignStmt? {
        if (parsed !is ExprAst.IndexExpr || !p.check(TokenType.EQUALS)) return null
        p.advance()
        val rhs = expr(allowBareIdent).parse()
        return StmtAst.AssignStmt(
            lhs = LValueAst.IndexLValue(parsed.target, parsed.index, parsed.position),
            rhs = rhs,
            position = stmtPos.endingAt(rhs.range.end),
        )
    }

    /** 解析 `state.<name> = expr`。 */
    private fun parseStateAssign(allowBareIdent: Boolean): StmtAst.AssignStmt {
        val stateTok = p.expect(TokenType.KW_STATE, "state.* 赋值缺少 'state'")
        p.expect(TokenType.DOT, "state 后缺少 '.'")
        val nameTok = p.expect(TokenType.IDENTIFIER, "state 后缺少字段名")
        p.rejectReservedUserIdentifier(nameTok.lexeme, nameTok.position)
        p.expect(TokenType.EQUALS, "赋值语句缺少 '='")
        val rhs = expr(allowBareIdent).parse()
        val lhs = LValueAst.StateLValue(nameTok.lexeme, stateTok.position.endingAt(nameTok.range.end))
        return StmtAst.AssignStmt(lhs, rhs, stateTok.position.endingAt(rhs.range.end))
    }

    /**
     * 解析 `session.<name> = expr` / `persist.<name> = expr`。
     *
     * 与 [parseStateAssign] 同形态，仅根据 scopeToken 选择 LValue 类型。
     */
    private fun parseScopedAssign(
        scopeToken: TokenType,
        scopeName: String,
        allowBareIdent: Boolean,
    ): StmtAst.AssignStmt {
        val tok = p.expect(scopeToken, "$scopeName.* 赋值缺少 '$scopeName'")
        p.expect(TokenType.DOT, "$scopeName 后缺少 '.'")
        val nameTok = p.expect(TokenType.IDENTIFIER, "$scopeName 后缺少字段名")
        p.rejectReservedUserIdentifier(nameTok.lexeme, nameTok.position)
        val fieldName = if (
            p.isStrictMode()
            && (scopeToken == TokenType.KW_SESSION || scopeToken == TokenType.KW_PERSIST)
            && p.check(TokenType.DOT)
            && p.peek(1).type == TokenType.IDENTIFIER
        ) {
            p.advance()
            val fieldTok = p.expect(TokenType.IDENTIFIER, "$scopeName 命名空间后缺少字段名")
            p.rejectReservedUserIdentifier(fieldTok.lexeme, fieldTok.position)
            "${nameTok.lexeme}.${fieldTok.lexeme}"
        } else {
            nameTok.lexeme
        }
        val lhsPosition = tok.position.endingAt(p.previous().range.end)
        p.expect(TokenType.EQUALS, "赋值语句缺少 '='")
        val rhs = expr(allowBareIdent).parse()
        val lhs = when (scopeToken) {
            TokenType.KW_SESSION -> LValueAst.SessionLValue(fieldName, lhsPosition)
            TokenType.KW_PERSIST -> LValueAst.PersistLValue(fieldName, lhsPosition)
            else -> error("不支持的 scopeToken: $scopeToken")
        }
        return StmtAst.AssignStmt(lhs, rhs, tok.position.endingAt(rhs.range.end))
    }

    // ===== C2：for-in / when =====

    private fun parseForIn(allowBareIdent: Boolean): StmtAst.ForInStmt {
        val forTok = p.expect(TokenType.KW_FOR)
        p.expect(TokenType.LPAREN, "for 后缺少 '('")
        val nameTok = p.expect(TokenType.IDENTIFIER, "for 缺少循环变量名")
        p.expect(TokenType.KW_IN, "for 缺少 'in'")
        val iter = expr(allowBareIdent).parse()
        p.expect(TokenType.RPAREN, "for 头部缺少 ')'")
        p.skipNewlines()

        // body 拥有独立作用域，循环变量声明在 body 作用域中；for 计入 loopDepth
        p.expect(TokenType.LBRACE, "for 体缺少 '{'")
        p.skipNewlines()
        p.pushBlockScope()
        if (!p.declareLocal(nameTok.lexeme)) {
            p.fail("for 循环变量 '${nameTok.lexeme}' 重复声明", nameTok.position)
        }
        p.enterLoop()
        val body = try {
            parseStmtListUntil(TokenType.RBRACE, allowBareIdent)
        } finally {
            p.exitLoop()
            p.popBlockScope()
        }
        p.expect(TokenType.RBRACE, "for 体缺少 '}'")
        return StmtAst.ForInStmt(nameTok.lexeme, iter, body, p.completePosition(forTok.position))
    }

    /** 语句位置的 when：直接复用 WhenExpr 结构再包一层 WhenStmt。 */
    private fun parseWhenStmt(allowBareIdent: Boolean): StmtAst.WhenStmt {
        val we = parseWhenExprNode(allowBareIdent)
        return StmtAst.WhenStmt(we, we.position)
    }

    /**
     * 解析 `when (subject) { values -> { body } ... else -> { body } }`。
     *
     * - 分支 values：`literal | literal, literal, ...` —— 仅支持字面量（含 null），逗号分隔
     * - 分支体强制使用 `{ }` 块
     * - else 必须放在最后一个分支，至多 1 个
     */
    private fun parseWhenExprNode(allowBareIdent: Boolean): ExprAst.WhenExpr {
        val whenTok = p.expect(TokenType.KW_WHEN)
        p.expect(TokenType.LPAREN, "when 后缺少 '('")
        val subject = expr(allowBareIdent).parse()
        p.expect(TokenType.RPAREN, "when 主体缺少 ')'")
        p.skipNewlines()
        p.expect(TokenType.LBRACE, "when 缺少 '{'")
        p.skipNewlines()

        val branches = mutableListOf<ExprAst.WhenExpr.WhenBranch>()
        var elseBody: List<StmtAst>? = null
        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            if (elseBody != null) {
                p.fail("when 的 else 分支必须放在最后", p.current().position)
            }
            if (p.check(TokenType.KW_ELSE)) {
                elseBody = parseWhenElseBody(allowBareIdent)
                p.skipNewlines()
                continue
            }
            branches += parseWhenBranch(allowBareIdent)
            p.skipNewlines()
        }
        p.expect(TokenType.RBRACE, "when 缺少 '}'")
        return ExprAst.WhenExpr(subject, branches, elseBody, p.completePosition(whenTok.position))
    }

    /** 解析 when 的 else 分支体（已知当前 token 为 KW_ELSE）。 */
    private fun parseWhenElseBody(allowBareIdent: Boolean): List<StmtAst> {
        p.advance() // KW_ELSE
        p.expect(TokenType.ARROW, "when else 分支缺少 '->'")
        p.skipNewlines()
        return parseBranchBody(allowBareIdent)
    }

    /** 解析 when 的一条普通分支：`v1[, v2, ...] -> { body }`。 */
    private fun parseWhenBranch(allowBareIdent: Boolean): ExprAst.WhenExpr.WhenBranch {
        val branchPos = p.current().position
        val values = mutableListOf<ExprAst>()
        values += parseWhenValue(allowBareIdent)
        while (p.check(TokenType.COMMA)) {
            p.advance()
            p.skipNewlines()
            values += parseWhenValue(allowBareIdent)
        }
        p.expect(TokenType.ARROW, "when 分支缺少 '->'")
        p.skipNewlines()
        val body = parseBranchBody(allowBareIdent)
        return ExprAst.WhenExpr.WhenBranch(values, body, branchPos)
    }

    /** 仅接受字面量（含 null）作为 when pattern 值。 */
    private fun parseWhenValue(allowBareIdent: Boolean): ExprAst {
        val e = expr(allowBareIdent).parse()
        if (e !is ExprAst.Literal && e !is ExprAst.NullLit) {
            p.fail("when 分支仅支持字面量 pattern", e.position)
        }
        return e
    }

    /**
     * 分支体强制使用 `{ }` 块；额外允许"非调用表达式"作为语句出现，
     * 用于 when 表达式形态把分支体末尾的裸表达式作为返回值。
     */
    private fun parseBranchBody(allowBareIdent: Boolean): List<StmtAst> {
        p.expect(TokenType.LBRACE, "when 分支缺少 '{'")
        p.skipNewlines()
        val out = withScopedBlock {
            parseStmtListUntil(TokenType.RBRACE, allowBareIdent, ::parseBranchStmt)
        }
        p.expect(TokenType.RBRACE, "when 分支缺少 '}'")
        return out
    }

    /** 分支语句：非语句起始 token 视为裸表达式语句（不要求 call 形式）。 */
    private fun parseBranchStmt(allowBareIdent: Boolean): StmtAst {
        val t = p.current().type
        // KW_IF：分支体为 `{` 走 if 语句，否则视为 if 表达式（用作分支返回值）
        if (t == TokenType.KW_IF) {
            return if (ifBranchLooksLikeBlock()) {
                parseStmt(allowBareIdent)
            } else {
                val pos = p.current().position
                val parsed = expr(allowBareIdent).parse()
                StmtAst.ExprStmt(parsed, pos.endingAt(parsed.range.end))
            }
        }
        // 数字/字符串/布尔/null/插值串/列表字面量/一元 等明显的表达式起始
        return when (t) {
            TokenType.NUMBER, TokenType.STRING, TokenType.BOOLEAN, TokenType.NULL,
            TokenType.INTERPOLATED_STRING_START, TokenType.LPAREN,
            TokenType.LBRACKET, TokenType.MINUS, TokenType.BANG -> {
                val pos = p.current().position
                val e = expr(allowBareIdent).parse()
                StmtAst.ExprStmt(e, pos.endingAt(e.range.end))
            }
            else -> parseStmt(allowBareIdent)
        }
    }

    /**
     * 解析 lambda 体（C3）：当前 token 已位于 `{ params -> ` 之后，止于 `}` 之前。
     *
     * - 为参数和 var 声明引入独立块作用域
     * - lambda 体可使用 `return`（视为 lambda 自身的返回值）
     * - 末尾允许裸表达式作为返回值（与 when 分支体一致），且允许 IDENTIFIER /
     *   KW_STATE 等表达式起始 token 直接作为表达式语句
     */
    internal fun parseLambdaBody(params: List<String>, allowBareIdent: Boolean): List<StmtAst> =
        withScopedBlock(
            enterReturnable = true,
            setup = { for (n in params) p.declareLocal(n) },
        ) {
            parseStmtListUntil(TokenType.RBRACE, allowBareIdent, ::parseLambdaStmt)
        }

    /**
     * lambda 体内的单条语句：相比标准 parseStmt 更宽松，允许任意表达式作为语句
     * （便于把末位表达式作为返回值）。仍优先识别 var / if / 局部赋值 / state 赋值
     * 等控制结构。
     */
    private fun parseLambdaStmt(allowBareIdent: Boolean): StmtAst {
        val t = p.current().type
        // KW_IF：分支体为 `{` 走 if 语句；否则作为 if 表达式（lambda 末位返回值）
        if (t == TokenType.KW_IF) {
            return if (ifBranchLooksLikeBlock()) {
                parseStmt(allowBareIdent)
            } else {
                val pos = p.current().position
                val parsed = expr(allowBareIdent).parse()
                StmtAst.ExprStmt(parsed, pos.endingAt(parsed.range.end))
            }
        }
        // 这些 token 必然走标准语句路径
        when (t) {
            TokenType.KW_VAR,
            TokenType.KW_WHILE, TokenType.KW_FOR, TokenType.KW_WHEN,
            TokenType.KW_BREAK, TokenType.KW_CONTINUE, TokenType.KW_RETURN,
            TokenType.KW_TRY, TokenType.KW_FUN -> return parseStmt(allowBareIdent)
            else -> Unit
        }
        // 局部变量赋值 `x = expr`，或 `suspend fun NAME(...)` 局部 suspend fun
        if (t == TokenType.IDENTIFIER) {
            val cur = p.current()
            if (isParamsAssignStart()) {
                return parseParamsAssignReject()
            }
            if (p.isLocal(cur.lexeme) && p.peek(1).type == TokenType.EQUALS) {
                return parseStmt(allowBareIdent)
            }
            // M5-e：识别 `suspend fun` 软关键字前缀（与标准 parseStmt 行为对齐）
            if (cur.lexeme == SOFT_KW_SUSPEND && p.peek(1).type == TokenType.KW_FUN) {
                return parseStmt(allowBareIdent)
            }
            // `batch { ... }` 合批写入块：走标准语句路径，避免被当成 batch{lambda} 调用
            if (cur.lexeme == SOFT_KW_BATCH && p.peek(1).type == TokenType.LBRACE) {
                return parseStmt(allowBareIdent)
            }
        }
        // state / session / persist 赋值 `state.x = expr` / `session.x = expr` / `persist.x = expr`
        if (isScopedAssignStart(t)) {
            return parseStmt(allowBareIdent)
        }
        // 其余一律视为裸表达式语句
        val pos = p.current().position
        val e = expr(allowBareIdent).parse()
        // M6-a：lambda 体内同样允许 `m["k"] = v` 索引写入。
        tryIndexAssign(e, pos, allowBareIdent)?.let { return it }
        if (isBareLocalApiAccess(e)) {
            p.fail("local API 语句缺少调用括号", pos)
        }
        return StmtAst.ExprStmt(e, pos.endingAt(e.range.end))
    }

    private fun isBareLocalApiAccess(expr: ExprAst): Boolean = when (expr) {
        is ExprAst.MemberAccess -> (expr.target as? ExprAst.IdentRef)?.name == "local"
        is ExprAst.SafeMemberAccess -> (expr.target as? ExprAst.IdentRef)?.name == "local"
        else -> false
    }

    private fun isParamsAssignStart(): Boolean =
        p.current().type == TokenType.IDENTIFIER &&
            p.current().lexeme == "params" &&
            p.peek(1).type == TokenType.DOT &&
            p.peek(2).type == TokenType.IDENTIFIER &&
            p.peek(3).type == TokenType.EQUALS

    private fun parseParamsAssignReject(): Nothing {
        val paramsTok = p.expect(TokenType.IDENTIFIER, "params 赋值缺少关键字")
        p.expect(TokenType.DOT, "params 后缺少 '.'")
        val nameTok = p.expect(TokenType.IDENTIFIER, "params 后缺少字段名")
        p.fail(DslError.AUI_V2_PARAM_READONLY, paramsTok.position, "params.${nameTok.lexeme}")
    }

    private fun isScopedAssignStart(t: TokenType): Boolean {
        if (t != TokenType.KW_STATE && t != TokenType.KW_SESSION && t != TokenType.KW_PERSIST) return false
        if (p.peek(1).type != TokenType.DOT || p.peek(2).type != TokenType.IDENTIFIER) return false
        if (p.peek(3).type == TokenType.EQUALS) return true
        return p.isStrictMode()
            && (t == TokenType.KW_SESSION || t == TokenType.KW_PERSIST)
            && p.peek(3).type == TokenType.DOT
            && p.peek(4).type == TokenType.IDENTIFIER
            && p.peek(5).type == TokenType.EQUALS
    }
}
